import {
    Component,
    OnInit,
    OnDestroy,
    EventEmitter,
    Input,
    Output,
    SecurityContext,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslationStorageService } from '@app/services';
import { FlinksConnect } from '@models/company/flinks-connect.model';
import { TranslateModule } from '@ngx-translate/core';
import { BannerComponent } from '@app/components';

type FlinksIframeSrcResponse = {
    data: {
        attributes: {
            url: string;
        };
    };
};

@Component({
    standalone: true,
    selector: 'app-flinks-iframe',
    template: `@if (iframeError) {
            <ui-banner variant="danger">{{ 'company.overview.bankAccountError' | translate }}</ui-banner>
        } @else if (iframeReady) {
            <iframe class="w-100" height="760" [src]="iframeSrc"></iframe>
        }`,
    imports: [TranslateModule, BannerComponent],
})
export class FlinksIframeView implements OnInit, OnDestroy, OnChanges {
    @Input() isOpen!: boolean;
    @Input() companyId!: number;
    @Output() accountConnectionTriggered = new EventEmitter<void>();

    iframeUrl: URL | undefined;
    iframeSrc: SafeResourceUrl | undefined;
    iframeReady = false;
    iframeError = false;

    constructor(
        private translationStorage: TranslationStorageService,
        private http: HttpClient,
        private sanitizer: DomSanitizer
    ) {}

    ngOnInit(): void {
        window.addEventListener('message', this.handleMessageEvent);
    }

    /**
     * Detect when the dialog is opened (isOpen changes from false → true).
     * This ensures the iframe URL is rebuilt each time the dialog is reopened, preventing token expiration.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
            this.resetIframe();
            this.buildIframeUrl();
        }
    }

    ngOnDestroy(): void {
        window.removeEventListener('message', this.handleMessageEvent);
    }

    private resetIframe(): void {
        this.iframeUrl = undefined;
        this.iframeSrc = undefined;
        this.iframeReady = false;
        this.iframeError = false;
    }

    private async buildIframeUrl(): Promise<void> {
        try {
            const response = await this.http
                .get<FlinksIframeSrcResponse>(
                    `/v2/companyManagement/companies/${this.companyId}/bankAccounts/flinksIframeSrc`
                )
                .toPromise();
            this.iframeUrl = new URL(response.data.attributes.url);
            this.iframeUrl.searchParams.append('customerName', 'Humi');
            this.iframeUrl.searchParams.append('staticLoadingEnable', 'true');
            this.iframeUrl.searchParams.append('accountSelectorEnable', 'true');
            this.iframeUrl.searchParams.append('accountSelectorCurrency', 'cad');
            this.iframeUrl.searchParams.append('withTransactions', 'false');
            this.iframeUrl.searchParams.append('tag', `HCID=${this.companyId}`);
            this.iframeUrl.searchParams.append('language', this.getLanguage());

            const iFrameSrc = this.sanitizer.sanitize(SecurityContext.URL, this.iframeUrl.toString()) ?? '';
            if (iFrameSrc) {
                this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(iFrameSrc);
                this.iframeReady = true;
            } else {
                this.iframeError = true;
            }
        } catch (error) {
            this.iframeError = true;
        }
    }

    private getLanguage(): string {
        const locale = this.translationStorage.locale;
        return locale && locale.toLocaleLowerCase().startsWith('fr') ? 'fr' : 'en';
    }

    private handleMessageEvent = (event: MessageEvent): void => {
        if (!this.iframeUrl) {
            return;
        }

        if (event.origin !== `${this.iframeUrl.protocol}//${this.iframeUrl.host}`) {
            return;
        }

        if (event.data.step === 'REDIRECT') {
            new FlinksConnect({ flinksLoginId: event.data.loginId, flinksAccountId: event.data.accountId })
                .param('company', this.companyId)
                .create()
                .then(() => {
                    this.accountConnectionTriggered.emit();
                })
                .catch(() => {
                    this.iframeError = true;
                });
        }
    };
}
